import { useHirerAuth } from '@seek/hirer-auth-react';
import { useAccountContext } from '@seek/hirer-graphql-react';
import { Page } from 'braid-design-system';
import React, { type ReactNode } from 'react';

import type { ActiveTab, ActiveSubTab } from 'src/shared/types';
import { brandLookup } from 'src/utils/brandLookup';

import { useConfig } from '../../shared/hooks/';
import { usePageTracking } from '../../shared/hooks/usePageTracking';
import type { PageTrackingProps } from '../Tracking/trackingTypes';

import '../../shared/polyfills';

import AppFooter from './AppFooter/AppFooter';
import AppHeader from './AppHeader/AppHeader';
import { PageMeta } from './PageMeta';

export interface StaticPageProviderProps extends PageTrackingProps {
  title: string;
  returnUrl: string;
  children: ReactNode;
  basic?: boolean;
  activeTab?: ActiveTab;
  activeSubTab?: ActiveSubTab;
  hideHeader?: boolean;
  hideFooter?: boolean;
  shouldTrack?: boolean;
  description?: string;
  index?: boolean;
  follow?: boolean;
  enableHreflang?: boolean;
}

const StaticPageProvider = ({
  title,
  children,
  basic,
  activeTab,
  eventName,
  activeSubTab,
  siteSection,
  subSection,
  hideHeader,
  hideFooter,
  currentPage,
  linkName,
  jobId,
  errorMessage,
  shouldTrack = true,
  description,
  index,
  follow,
  enableHreflang,
}: StaticPageProviderProps) => {
  const {
    siteLanguage,
    site,
    country,
    language,
    environment,
    zone,
    hostname,
    route,
    classification,
  } = useConfig();
  const { authenticated, loading } = useHirerAuth();
  const { accountContext, loading: accountContextLoading } = useAccountContext({
    skip: !authenticated || loading,
  });

  const brand = brandLookup(site).toLocaleLowerCase();

  const isStaging = environment === 'staging';
  const headerFooterProps = {
    site,
    locale: language,
    isStaging,
  };

  const pageTrackingProps = {
    eventName,
    siteSection,
    siteCountry: country,
    isLoggedIn: authenticated,
    ...(subSection && { subSection }),
    ...(brand && { brand }),
    ...(currentPage && { currentPage }),
    ...(siteLanguage && { siteLanguage }),
    ...(linkName && { linkName }),
    ...(jobId && { jobId }),
    ...(zone && { zone }),
    ...(errorMessage && { errorMessage }),
    ...(accountContext?.currentAdvertiser?.billingId && {
      seekAdvertiserId: Number(accountContext.currentAdvertiser.billingId),
    }),
    ...(accountContext?.currentUser?.id && {
      userId: accountContext.currentUser.id,
    }),
  };

  usePageTracking({
    ...pageTrackingProps,
    shouldTrack: shouldTrack && !loading && !accountContextLoading,
    accountContext,
    authenticated,
    loading: loading || accountContextLoading,
    page: currentPage || '',
  });

  return (
    <Page
      footer={
        hideFooter ? null : (
          <AppFooter {...headerFooterProps} {...accountContext} />
        )
      }
    >
      {hideHeader ? null : (
        <AppHeader
          basic={basic}
          activeTab={activeTab}
          activeSubTab={activeSubTab}
          {...headerFooterProps}
          {...accountContext}
        />
      )}

      <PageMeta
        title={title}
        description={description}
        index={index}
        follow={follow}
        enableHreflang={enableHreflang}
        route={route}
        hostname={hostname}
        siteLanguage={siteLanguage}
        classification={classification}
      />

      {children}
    </Page>
  );
};

export default StaticPageProvider;
